import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { CaretRight, CaretDown, Package, ArrowsOutCardinal, DotsThree } from "@phosphor-icons/react";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import MoveToDialog from "../components/MoveToDialog";

function subtreeMatches(node, q) {
  if (!q) return true;
  const text = `${node.asset_number} ${node.name}`.toLowerCase();
  if (text.includes(q)) return true;
  return (node.children || []).some((c) => subtreeMatches(c, q));
}

// Flatten the tree into rows with depth, respecting expand/collapse and search.
function buildRows(nodes, expanded, q, depth, out) {
  nodes.forEach((n) => {
    if (!subtreeMatches(n, q)) return;
    out.push({ node: n, depth });
    const isOpen = q ? true : (expanded[n.id] ?? depth < 1);
    if (isOpen && (n.children || []).length) buildRows(n.children, expanded, q, depth + 1, out);
  });
  return out;
}

function Row({ node, depth, isOpen, hasKids, onToggle, onOpen, onMove, canMove }) {
  const drag = useDraggable({ id: node.id, disabled: !canMove });
  const drop = useDroppable({ id: node.id });
  const cust = node.custodian;
  return (
    <div ref={drop.setNodeRef}
      className={`flex items-center gap-1 rounded ${drop.isOver ? "bg-blue-100 ring-2 ring-blue-400" : ""}`}
      style={{ paddingLeft: depth * 18 }}>
      <button onClick={() => onToggle(node.id)} data-testid={`tree-toggle-${node.id}`}
        className={`w-5 h-6 flex items-center justify-center text-slate-400 ${hasKids ? "" : "invisible"}`}>
        {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      <div ref={drag.setNodeRef} {...drag.listeners} {...drag.attributes}
        className={`flex-1 flex items-center gap-2 py-1.5 px-2 rounded cursor-grab active:cursor-grabbing ${drag.isDragging ? "opacity-50" : ""}`}>
        <Package size={16} className="text-slate-400 shrink-0" />
        <button data-testid={`tree-open-${node.id}`} onClick={() => onOpen(node.id)} className="text-left min-w-0">
          <span className="font-mono text-xs text-slate-500">{node.asset_number}</span>{" "}
          <span className="font-semibold text-slate-800">{node.name}</span>
          {node.child_count > 0 && <span className="text-xs text-slate-400"> · contains {node.child_count}</span>}
        </button>
        {node.oos && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 rounded px-1.5">OOS</span>}
        {cust && cust.name && <span className="text-xs text-slate-400 truncate hidden sm:inline">{cust.name}{cust.source === "inherited" ? " (via parent)" : ""}</span>}
        {node.location_name && <span className="text-xs text-slate-400 hidden md:inline">· {node.location_name}</span>}
      </div>
      {canMove && <button data-testid={`tree-move-${node.id}`} onClick={() => onMove(node)}
        className="text-slate-400 hover:text-slate-700 px-1" title="Move to…"><DotsThree size={18} weight="bold" /></button>}
    </div>
  );
}

function TopDrop() {
  const drop = useDroppable({ id: "__TOP__" });
  return <div ref={drop.setNodeRef} data-testid="tree-top-drop"
    className={`border-2 border-dashed rounded-lg px-3 py-2 text-sm text-slate-500 ${drop.isOver ? "border-blue-400 bg-blue-50" : "border-slate-300"}`}>
    Drop here to move an item to top level (remove from its container)</div>;
}

export default function AssetTreePage() {
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const canMove = hasPerm("asset_manager", "administrator", "supervisor");
  const [tree, setTree] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [moveTarget, setMoveTarget] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }));

  const load = useCallback(() => api.get("/assets/tree").then((r) => setTree(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !(e[id] ?? false) }));

  const onDragEnd = async (evt) => {
    const activeId = evt.active && evt.active.id;
    const overId = evt.over && evt.over.id;
    if (!activeId || !overId || activeId === overId) return;
    try {
      const payload = overId === "__TOP__" ? { to_top_level: true } : { container_asset_id: overId };
      await api.post(`/assets/${activeId}/move`, payload);
      toast.success("Moved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid move"); }
  };

  const ql = q.trim().toLowerCase();
  const rows = buildRows(tree, expanded, ql, 0, []);

  return (
    <div className="flex flex-col gap-4" data-testid="asset-tree-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Asset Tree</h1>
        <Input data-testid="tree-search" placeholder="Search all assets…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" />
      </div>
      {canMove && <p className="text-xs text-slate-500 flex items-center gap-1"><ArrowsOutCardinal size={14} />Drag an item onto a container to move it — or use the ⋯ Move to… button (best on mobile).</p>}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="bg-white border-2 border-slate-200 rounded-lg p-2 flex flex-col gap-0.5">
          {rows.length === 0 ? <p className="text-slate-400 text-sm p-3">No assets.</p> : rows.map(({ node, depth }) => {
            const hasKids = (node.children || []).length > 0;
            const isOpen = ql ? true : (expanded[node.id] ?? depth < 1);
            return <Row key={node.id} node={node} depth={depth} isOpen={isOpen} hasKids={hasKids}
              onToggle={toggle} onOpen={(id) => nav(`/assets/${id}`)} onMove={setMoveTarget} canMove={canMove} />;
          })}
        </div>
        {canMove && <TopDrop />}
      </DndContext>
      {moveTarget && <MoveToDialog asset={moveTarget} open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)} onMoved={load} />}
    </div>
  );
}
